/**
 * imageDpiAutoSize extension (editor-image-tools dpi) — **삽입 직후** PNG 이미지의 표시 크기를
 * 물리 해상도(dpi)로 보정한다. 기존 tinymce 의 `setImgMaxSize`(자동 크기에 `naturalWidth × 72/dpi`
 * 적용)와 동일한 결과를 모든 삽입 경로(파일첨부/붙여넣기/임시저장 복원)에서 일괄 재현한다.
 *
 * 동작 원리(라이브러리 ImageNodeView `handleImageLoad` 분석 기반):
 *   - 라이브러리는 image 노드 `width` 가 미설정이면 이미지 로드 후 naturalWidth(에디터폭 clamp)로
 *     자동 설정한다. 그러나 **width 가 이미 설정되어 있고 editorWidth 이하이면 덮어쓰지 않는다.**
 *   - 따라서 본 plugin 이 라이브러리보다 먼저(트랜잭션 직후, 이미지 onload 이전) dpi 보정된 width 를
 *     주입하면 라이브러리가 이를 존중한다. 보정 판정·계산은 `NgwEditorImage.autoDpiSizeForInsert`.
 *   - width 가 이미 설정된 노드(라이브러리 자동/사용자 드래그·프리셋)는 대상에서 제외되므로,
 *     update 중 dispatch 가 유발하는 재진입은 즉시 수렴한다(무한 루프 없음).
 *
 * 구현 패턴은 `imageSelection` 과 동일 — `@ngweditor/react` 가 PM `Plugin` 을 runtime export 하지
 * 않으므로 `view.state.plugins[0].constructor`(BundledPlugin)로 plugin view 인스턴스를 생성해 주입.
 */

import type { EditorView, NgwExtension, Plugin } from '@ngweditor/react'
import type { NgwEditorAdapter } from '../NgwEditorAdapter'

const IMAGE_NODE_NAME = 'image'

/** BundledPlugin duck-typing — plugin view 의 update 로 문서 변경을 감지. */
interface DpiAutoSizePluginSpec {
  view?: (view: EditorView) => {
    update: (view: EditorView) => void
    destroy?: () => void
  }
}

/** PM 노드/트랜잭션 최소 구조(런타임 클래스 미export 회피). */
interface PMNode {
  type: { name: string }
  attrs: Record<string, unknown>
}
interface PMTransaction {
  setNodeMarkup(pos: number, type: undefined, attrs: Record<string, unknown>): PMTransaction
}
interface PMDocView extends EditorView {
  state: EditorView['state'] & {
    doc: { descendants(f: (node: PMNode, pos: number) => void): void }
    tr: PMTransaction
  }
  dispatch(tr: PMTransaction): void
}

export function createImageDpiAutoSizeExtension(adapter: NgwEditorAdapter): NgwExtension {
  return {
    name: 'image-dpi-auto-size',
    setup(ctx) {
      const view = ctx.editor.view
      const firstPlugin = view.state.plugins[0]
      const BundledPlugin = firstPlugin?.constructor as
        | (new (spec: DpiAutoSizePluginSpec) => Plugin)
        | undefined
      if (BundledPlugin == null) {
        return
      }

      const dpiPlugin = new BundledPlugin({
        view: () => ({
          update: (pluginView) => applyDpiAutoSize(adapter, pluginView as PMDocView),
        }),
      })

      view.updateState(
        view.state.reconfigure({
          plugins: [...view.state.plugins, dpiPlugin],
        }),
      )
    },
  }
}

/** 문서 내 width 미설정 PNG image 노드에 dpi 보정 크기를 1회 주입(한 트랜잭션에 모아 dispatch). */
function applyDpiAutoSize(adapter: NgwEditorAdapter, view: PMDocView): void {
  let tr: PMTransaction | null = null
  view.state.doc.descendants((node, pos) => {
    if (node.type.name !== IMAGE_NODE_NAME) {
      return
    }
    const src = node.attrs.src
    if (typeof src !== 'string') {
      return
    }
    const size = adapter.image.autoDpiSizeForInsert(src, node.attrs.width, node.attrs.dataNineSize)
    if (size == null) {
      return
    }
    tr = (tr ?? view.state.tr).setNodeMarkup(pos, undefined, {
      ...node.attrs,
      width: size.width,
      height: size.height,
    })
  })
  if (tr != null) {
    view.dispatch(tr)
  }
}
